/**
 * SEO Visibility Dashboard
 *
 * Story 56: Track Product SEO Visibility in Google Search
 *
 * Dashboard showing:
 * - Summary stats (tracked products, avg position, top 10 count)
 * - Position trend charts
 * - Tracked products table
 * - Ranking change alerts
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Search,
  Bell,
  Eye,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  RefreshCw,
  Download,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useSite } from '@/contexts/SiteContext';
import {
  getDashboardData,
  getPositionHistory,
  markAlertsAsRead,
  DashboardData,
  DashboardProduct,
  VisibilityAlert,
  PositionHistoryEntry,
} from '@/services/productVisibilityService';
import { useToast } from '@/components/ui/use-toast';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { VisibilityPanel } from '@/components/product/VisibilityPanel';

function PositionBadge({ position }: { position: number | null }) {
  if (position === null) {
    return (
      <span className="text-muted-foreground">-</span>
    );
  }

  if (position <= 3) {
    return (
      <Badge className="bg-green-600 font-mono">#{position}</Badge>
    );
  }

  if (position <= 10) {
    return (
      <Badge className="bg-green-500 font-mono">#{position}</Badge>
    );
  }

  if (position <= 20) {
    return (
      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 font-mono">#{position}</Badge>
    );
  }

  return (
    <Badge variant="outline" className="font-mono text-muted-foreground">#{position}</Badge>
  );
}

function ChangeIndicator({ change }: { change: number | null }) {
  if (change === null || change === 0) {
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  }

  if (change > 0) {
    return (
      <span className="flex items-center text-green-600 text-sm font-medium">
        <ArrowUp className="h-3 w-3 mr-0.5" />
        +{change}
      </span>
    );
  }

  return (
    <span className="flex items-center text-red-600 text-sm font-medium">
      <ArrowDown className="h-3 w-3 mr-0.5" />
      {change}
    </span>
  );
}

export default function VisibilityDashboard() {
  const { selectedSite } = useSite();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<number>(30);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [productHistory, setProductHistory] = useState<PositionHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Visibility panel state
  const [visibilityPanelOpen, setVisibilityPanelOpen] = useState(false);
  const [visibilityProduct, setVisibilityProduct] = useState<DashboardProduct | null>(null);

  // Load dashboard data
  useEffect(() => {
    if (selectedSite?.id) {
      loadDashboard();
    }
  }, [selectedSite?.id, dateRange]);

  // Load product from URL params
  useEffect(() => {
    const productParam = searchParams.get('product');
    if (productParam) {
      setSelectedProduct(productParam);
    }
  }, [searchParams]);

  // Load product history when selected
  useEffect(() => {
    if (selectedProduct) {
      loadProductHistory(selectedProduct);
    }
  }, [selectedProduct, dateRange]);

  const loadDashboard = async (refresh = false) => {
    if (!selectedSite?.id) return;

    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const data = await getDashboardData(selectedSite.id, dateRange);
      setDashboardData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadProductHistory = async (productId: string) => {
    try {
      setHistoryLoading(true);
      const data = await getPositionHistory(productId, dateRange);
      setProductHistory(data.history);
    } catch (err) {
      console.error('Failed to load history:', err);
      setProductHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleMarkAlertsRead = async () => {
    if (!dashboardData) return;

    const unreadIds = dashboardData.alerts
      .filter(a => !a.is_read)
      .map(a => a.id);

    if (unreadIds.length === 0) return;

    try {
      await markAlertsAsRead(unreadIds);
      toast({
        title: 'Alerts marked as read',
        description: `${unreadIds.length} alerts marked as read`,
      });
      loadDashboard(true);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to mark alerts as read',
        variant: 'destructive',
      });
    }
  };

  const handleExport = () => {
    if (!dashboardData) return;

    const csvRows = [
      ['Product Name', 'Brand Position', 'Position Change', 'Last Checked'],
      ...dashboardData.products.map(p => [
        p.product_name,
        p.brand_position?.toString() || 'Not found',
        p.positionChange?.toString() || '0',
        p.last_visibility_check || 'Never',
      ]),
    ];

    const csv = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seo-visibility-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!selectedSite) {
    return (
      <div className="container mx-auto p-6">
        <Alert>
          <AlertDescription>Please select a site to view SEO visibility data.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Search className="h-8 w-8" />
            SEO Visibility Dashboard
          </h1>
          <p className="text-muted-foreground mt-2">
            Track product rankings in Google search for {selectedSite.name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange.toString()} onValueChange={(v) => setDateRange(parseInt(v))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExport} disabled={!dashboardData}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => loadDashboard(true)} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-80" />
          <Skeleton className="h-96" />
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : dashboardData ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Products Tracked
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{dashboardData.summary.totalTracked}</div>
                <p className="text-xs text-muted-foreground">
                  {dashboardData.summary.checkedToday} checked today
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Average Position
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {dashboardData.summary.avgPosition || '-'}
                </div>
                <p className="text-xs text-muted-foreground">Brand search</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Top 10 Rankings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  {dashboardData.summary.top10Count}
                </div>
                <p className="text-xs text-muted-foreground">
                  {dashboardData.summary.totalTracked > 0
                    ? Math.round((dashboardData.summary.top10Count / dashboardData.summary.totalTracked) * 100)
                    : 0}% of tracked
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Not Ranking
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-red-600">
                  {dashboardData.summary.notRankingCount}
                </div>
                <p className="text-xs text-muted-foreground">Not in top 100</p>
              </CardContent>
            </Card>
          </div>

          {/* Alerts Section */}
          {dashboardData.unreadAlertCount > 0 && (
            <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
                    <Bell className="h-5 w-5" />
                    Ranking Alerts ({dashboardData.unreadAlertCount} unread)
                  </CardTitle>
                  <Button variant="outline" size="sm" onClick={handleMarkAlertsRead}>
                    Mark all read
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {dashboardData.alerts.filter(a => !a.is_read).slice(0, 5).map((alert) => (
                    <div
                      key={alert.id}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        alert.alert_type === 'improvement'
                          ? 'bg-green-100 dark:bg-green-900/20'
                          : 'bg-red-100 dark:bg-red-900/20'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {alert.alert_type === 'improvement' ? (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-600" />
                        )}
                        <span className="font-medium">{alert.product_name}</span>
                        <Badge variant="outline">{alert.search_type}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm">
                          {alert.previous_position || '?'} → {alert.new_position || '?'}
                        </span>
                        <ChangeIndicator change={alert.position_change} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Position History Chart */}
          {selectedProduct && productHistory.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Position History</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedProduct(null)}
                  >
                    Clear selection
                  </Button>
                </div>
                <CardDescription>
                  {dashboardData.products.find(p => p.product_id === selectedProduct)?.product_name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {historyLoading ? (
                  <Skeleton className="h-64" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={productHistory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis
                        reversed
                        domain={[1, 100]}
                        tickFormatter={(value) => `#${value}`}
                      />
                      <Tooltip
                        formatter={(value: any, name: string) => [
                          value ? `#${value}` : 'Not found',
                          name === 'brand' ? 'Brand Search' : name === 'researchAvg' ? 'Research (avg)' : 'Question'
                        ]}
                        labelFormatter={(label) => new Date(label).toLocaleDateString()}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="brand"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={false}
                        name="Brand Search"
                      />
                      <Line
                        type="monotone"
                        dataKey="researchAvg"
                        stroke="#10b981"
                        strokeWidth={2}
                        dot={false}
                        name="Research (avg)"
                      />
                      <Line
                        type="monotone"
                        dataKey="question"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={false}
                        name="Question"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          )}

          {/* Tracked Products Table */}
          <Card>
            <CardHeader>
              <CardTitle>Tracked Products</CardTitle>
              <CardDescription>
                Click a product to view its position history
              </CardDescription>
            </CardHeader>
            <CardContent>
              {dashboardData.products.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No products tracked yet</p>
                  <p className="text-sm mt-1">
                    Go to The Offering and click "SEO" on any product to start tracking
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate('/about-business/the-offering')}
                  >
                    Go to The Offering
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-center">Brand Position</TableHead>
                      <TableHead className="text-center">Change</TableHead>
                      <TableHead className="text-center">Last Checked</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboardData.products.map((product) => (
                      <TableRow
                        key={product.product_id}
                        className={`cursor-pointer hover:bg-accent ${
                          selectedProduct === product.product_id ? 'bg-accent' : ''
                        }`}
                        onClick={() => setSelectedProduct(product.product_id)}
                      >
                        <TableCell>
                          <div className="font-medium">{product.product_name}</div>
                          {product.research_terms && product.research_terms.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              {product.research_terms.length} research terms tracked
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <PositionBadge position={product.brand_position} />
                        </TableCell>
                        <TableCell className="text-center">
                          <ChangeIndicator change={product.positionChange} />
                        </TableCell>
                        <TableCell className="text-center text-sm text-muted-foreground">
                          {product.last_visibility_check
                            ? new Date(product.last_visibility_check).toLocaleDateString()
                            : 'Never'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setVisibilityProduct(product);
                              setVisibilityPanelOpen(true);
                            }}
                            title="View SEO visibility details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}

      {/* Visibility Panel */}
      {visibilityProduct && selectedSite && (
        <VisibilityPanel
          productId={visibilityProduct.product_id}
          productName={visibilityProduct.product_name}
          productUrl=""
          siteId={selectedSite.id}
          open={visibilityPanelOpen}
          onOpenChange={setVisibilityPanelOpen}
          onTrackingChange={() => loadDashboard(true)}
        />
      )}
    </div>
  );
}
